import axios from 'axios';
import Cookies from 'js-cookie';

class TokenService {
    constructor() {
        this.refreshPromise = null;
        this.isRefreshing = false;
        this.lastRefreshTime = null; // track เวลา refresh ล่าสุด
        this.initializeTokenRefresh();
    }

    // Set token with proper expiration and sync storage
    setToken(token, uid = null) {
        if (!token) {
            console.error('❌ setToken called with no token');
            return false;
        }

        try {
            console.log('🔐 Setting token:', token.substring(0, 20) + '...');
            
            const expires = new Date();
            expires.setDate(expires.getDate() + 30); // 30 days from now

            // Set cookie with secure flags
            Cookies.set("jwt", token, { 
                expires: expires,
                secure: window.location.protocol === 'https:',
                sameSite: 'lax' 
            });

            // Sync with localStorage for backup
            localStorage.setItem("token", token);
            localStorage.setItem("token_expiry", expires.toISOString());
            
            if (uid) {
                localStorage.setItem("uidOfUser", uid);
                console.log('👤 UID set:', uid);
            }

            console.log('✅ Token set successfully');
            console.log('🍪 Cookie set:', Cookies.get("jwt") ? 'Success' : 'Failed');
            console.log('💾 localStorage set:', localStorage.getItem("token") ? 'Success' : 'Failed');
            
            return true;
        } catch (error) {
            console.error('❌ Error setting token:', error);
            return false;
        }
    }

    // Get token with fallback and validation
    getToken() {
        try {
            // Try cookie first (more secure)
            let token = Cookies.get("jwt");
            
            // Fallback to localStorage
            if (!token) {
                token = localStorage.getItem("token");
            }

            if (!token) return null;

            // Check localStorage expiry if exists
            const expiry = localStorage.getItem("token_expiry");
            if (expiry && new Date() > new Date(expiry)) {
                console.log('🔐 Token expired, clearing...');
                this.clearToken();
                return null;
            }

            return token;
        } catch (error) {
            console.error('Error getting token:', error);
            return null;
        }
    }

    // Validate token with server
    async validateToken() {
        const token = this.getToken();
        if (!token) return false;

        try {
            const response = await axios.post(`${process.env.REACT_APP_API}/auth/validate-token`, {}, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000
            });

            return response.data.valid;
        } catch (error) {
            console.log('🔐 Token validation failed:', error.response?.data?.error || error.message);
            return false;
        }
    }

    // Refresh token seamlessly
    async refreshToken(force = false) {
        // Prevent multiple concurrent refresh requests
        if (this.isRefreshing && !force) {
            return this.refreshPromise;
        }

        const currentToken = this.getToken();
        if (!currentToken && !force) {
            console.log('🔐 No token to refresh');
            return false;
        }

        this.isRefreshing = true;

        this.refreshPromise = new Promise(async (resolve) => {
            try {
                console.log('🔄 Refreshing token...');

                const response = await axios.post(`${process.env.REACT_APP_API}/auth/refresh-token`, {}, {
                    headers: { Authorization: `Bearer ${currentToken}` },
                    timeout: 15000
                });

                if (response.data.success) {
                    const newToken = response.data.token;
                    const uid = response.data.uid;

                    this.setToken(newToken, uid);
                    this.lastRefreshTime = Date.now();
                    console.log('✅ Token refreshed successfully');

                    this.isRefreshing = false;
                    resolve(true);
                } else {
                    throw new Error(response.data.error || 'Token refresh failed');
                }

            } catch (error) {
                console.error('🔐 Token refresh failed:', error.response?.data?.error || error.message);

                // ลบ token เฉพาะเมื่อ server บอกว่า token ไม่ valid (401/403)
                // ถ้าเป็น network error / timeout → เก็บ token เดิมไว้ก่อน ห้าม logout
                const status = error.response?.status;
                if (status === 401 || status === 403) {
                    this.clearToken();
                }
                this.isRefreshing = false;
                resolve(false);
            }
        });

        return this.refreshPromise;
    }

    // Clear all token data
    clearToken() {
        try {
            Cookies.remove("jwt");
            localStorage.removeItem("token");
            localStorage.removeItem("token_expiry");
            localStorage.removeItem("uidOfUser");
            localStorage.removeItem("194aa3da_username");
            localStorage.removeItem("194aa3da_password");
            localStorage.removeItem("profile_user");
            
            console.log('🧹 Token cleared');
        } catch (error) {
            console.error('Error clearing token:', error);
        }
    }

    // Check if user is logged in
    isLoggedIn() {
        return this.getToken() !== null;
    }

    // Get user ID
    getUserId() {
        return localStorage.getItem("uidOfUser");
    }

    // รอ refresh ที่กำลังทำงานอยู่ให้เสร็จ แล้วคืน token ปัจจุบัน
    // ใช้เรียกก่อน API call สำคัญ เช่น lead submission
    async ensureFreshToken() {
        if (this.isRefreshing) {
            console.log('⏳ Waiting for ongoing token refresh before proceeding...');
            await this.refreshPromise;
        }
        return this.getToken();
    }

    // Initialize automatic token refresh
    initializeTokenRefresh() {
        // Refresh token every 25 minutes (before 30-minute cache expires)
        setInterval(async () => {
            if (this.isLoggedIn()) {
                await this.refreshToken();
            }
        }, 25 * 60 * 1000); // 25 minutes

        // Handle page visibility change (mobile browser return)
        // จำกัดการ refresh ให้ห่างกันอย่างน้อย 5 นาที
        // กัน race condition กับ API call ที่กำลังทำงานอยู่
        const MIN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 นาที
        document.addEventListener('visibilitychange', async () => {
            if (!document.hidden && this.isLoggedIn()) {
                const now = Date.now();
                const timeSinceLastRefresh = this.lastRefreshTime
                    ? now - this.lastRefreshTime
                    : Infinity;

                if (timeSinceLastRefresh > MIN_REFRESH_INTERVAL) {
                    console.log('📱 Page visible - refreshing token...');
                    await this.refreshToken();
                } else {
                    console.log('📱 Page visible - token recently refreshed, skipping');
                }
            }
        });

        // Handle beforeunload to save state
        window.addEventListener('beforeunload', () => {
            // Sync token state before page unload
            const token = this.getToken();
            if (token) {
                localStorage.setItem('token_last_seen', new Date().toISOString());
            }
        });

        // Handle page load - check if we returned from background
        window.addEventListener('load', async () => {
            const lastSeen = localStorage.getItem('token_last_seen');
            const token = this.getToken();
            
            if (token && lastSeen) {
                const timeSinceLastSeen = new Date() - new Date(lastSeen);
                
                // If more than 5 minutes since last seen, refresh token
                if (timeSinceLastSeen > 5 * 60 * 1000) {
                    console.log('📱 Returned after', Math.round(timeSinceLastSeen / 60000), 'minutes - refreshing token...');
                    await this.refreshToken();
                }
            }
            
            localStorage.removeItem('token_last_seen');
        });
    }

    // Enhanced axios interceptor for automatic token handling
    setupAxiosInterceptor(axiosInstance = axios) {
        // Request interceptor - add token to requests
        axiosInstance.interceptors.request.use(
            (config) => {
                const token = this.getToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor - handle token expiry
        axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    // Try to refresh token
                    const refreshed = await this.refreshToken();
                    
                    if (refreshed) {
                        // Retry original request with new token
                        const newToken = this.getToken();
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axiosInstance(originalRequest);
                    } else {
                        // Refresh failed - allow guest access to continue
                        console.log('🔐 Token refresh failed - allowing guest access');
                        return Promise.reject(error);
                    }
                }

                return Promise.reject(error);
            }
        );
    }
}

// Create singleton instance
const tokenService = new TokenService();

// Setup axios interceptor globally
tokenService.setupAxiosInterceptor();

export default tokenService;